import React from "react";
import { DayPicker, useNavigation } from "react-day-picker";
import { useRillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import MarkAttendancePtView from "./MarkAttendancePtView";
import MarkAttendanceView from "./MarkAttendanceView";
import { dailyWeekdaySet } from "./recurring-weekday-encoding";
import SchedulingEmptyState from "./SchedulingEmptyState";
import { HistoryCardSkeleton } from "./SchedulingSkeletons";
import { navigateToSchedulingTabEdit } from "./scheduling-create-from-overview";
import { canManageSchedulingRecord } from "./scheduling-event-ownership";
import { getPtBookingRange, ptSessionSlotKey } from "./pt-session-slots";

// ─── Filter tabs ──────────────────────────────────────────────────────────────

const FILTER_TABS = [
  { id: "all", label: "All" },
  { id: "classes", label: "Classes" },
  { id: "pt-sessions", label: "PT sessions" },
  { id: "facilities", label: "Facilities" },
];

// ─── Date / time utilities ────────────────────────────────────────────────────

function parseHhMm(str) {
  if (!str || typeof str !== "string") return { h: 9, min: 0 };
  const p = str.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!p) return { h: 9, min: 0 };
  return { h: Number(p[1]), min: Number(p[2]) };
}

function parseYyyyMmDd(s) {
  if (!s || typeof s !== "string") return null;
  const m = s.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Monday = 0 … Sunday = 6 (matches PT session / facility window API) */
function mondayIndexFromDate(d) {
  return (d.getDay() + 6) % 7;
}

function formatAmPm(date) {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDuration(minutes, longMin = false) {
  if (minutes == null || Number.isNaN(Number(minutes))) return "—";
  const n = Number(minutes);
  const unit = longMin ? " min" : "m";
  if (n < 60) return `${n}${unit}`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}${unit}` : `${h}h`;
}

const RECURRENCE_LABELS = {
  one_time: "One-time",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

/** "Sept 8, 2026 (Wed)" for the meta row inside each card */
function formatEventDateLabel(d) {
  let month = d.toLocaleDateString("en-US", { month: "short" });
  if (month === "Sep") month = "Sept";
  const day = d.getDate();
  const year = d.getFullYear();
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" });
  return `${month} ${day}, ${year} (${weekday})`;
}

/** "Sept 8, 2026 Wednesday (Today)" for the date-group header */
function formatGroupDateLabel(d) {
  let month = d.toLocaleDateString("en-US", { month: "short" });
  if (month === "Sep") month = "Sept";
  const day = d.getDate();
  const year = d.getFullYear();
  const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
  const now = new Date();
  const yesterday = addDays(now, -1);
  let suffix = "";
  if (sameDay(d, now)) suffix = " (Today)";
  else if (sameDay(d, yesterday)) suffix = " (Yesterday)";
  return `${month} ${day}, ${year} ${weekday}${suffix}`;
}

function dateToYyyyMmDd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function formatDateDisplay(ymd) {
  const d = parseYyyyMmDd(ymd);
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function HistoryCalendarCaption(props) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const displayMonth = props?.displayMonth ?? new Date();
  const monthLabel = displayMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  return (
    <div className="create-class-modal-calendar-caption">
      <button
        type="button"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Previous month"
      >
        <img src="/img/new-icons/arrow-left.svg" alt="" />
      </button>
      <span className="create-class-modal-calendar-title">{monthLabel}</span>
      <button
        type="button"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Next month"
      >
        <img src="/img/new-icons/arrow-right.svg" alt="" />
      </button>
    </div>
  );
}

// ─── Event expansion ──────────────────────────────────────────────────────────

/** Expand a class template into past event occurrences between `scanStart` and `scanEnd`. */
function expandClassOccurrences(c, scanStart, scanEnd) {
  const rStart = parseYyyyMmDd(c.recurrence_start_date);
  if (!rStart) return [];

  const rEnd = parseYyyyMmDd(c.recurrence_end_date);
  const rType = c.recurrence_type ?? "weekly";
  const { h: th, min: tm } = parseHhMm(c.start_time ?? "09:00");
  const durMin = Number(c.duration_minutes) || 60;

  const endBound = rEnd ? new Date(rEnd) : null;
  if (endBound) endBound.setHours(23, 59, 59, 999);

  const ceiling = endBound
    ? new Date(Math.min(endBound.getTime(), scanEnd.getTime()))
    : new Date(scanEnd);

  // Start the loop from the later of the class start date or the scan window start
  const loopStart = new Date(Math.max(rStart.getTime(), scanStart.getTime()));
  loopStart.setHours(0, 0, 0, 0);

  const out = [];

  if (rType === "one_time") {
    const occDate = new Date(rStart);
    occDate.setHours(th, tm, 0, 0);
    if (
      occDate.getTime() >= scanStart.getTime() &&
      occDate.getTime() < scanEnd.getTime()
    ) {
      out.push({
        start: occDate,
        end: new Date(occDate.getTime() + durMin * 60000),
      });
    }
    return out;
  }

  // Daily can be restricted to specific weekdays; null ⇒ every day.
  const dailyWd = dailyWeekdaySet(c.recurrence_weekdays);

  for (let t = loopStart.getTime(); t <= ceiling.getTime(); t += 86400000) {
    const day = new Date(t);
    let ok = false;
    if (rType === "daily") {
      ok = !dailyWd || dailyWd.has(day.getDay());
    } else if (rType === "weekly") {
      ok = day.getDay() === rStart.getDay();
    } else if (rType === "monthly") {
      ok = day.getDate() === rStart.getDate();
    } else {
      ok = day.getDay() === rStart.getDay();
    }
    if (!ok) continue;
    const occDate = new Date(day);
    occDate.setHours(th, tm, 0, 0);
    if (
      occDate.getTime() < scanStart.getTime() ||
      occDate.getTime() >= scanEnd.getTime()
    )
      continue;
    out.push({
      start: occDate,
      end: new Date(occDate.getTime() + durMin * 60000),
    });
  }
  return out;
}

/** Extract `{ day_of_week, start_time, end_time }[]` from a PT/facility API document. */
function extractWindows(doc) {
  if (Array.isArray(doc.windows) && doc.windows.length > 0) return doc.windows;
  const wa = doc.weekly_availability ?? doc.weeklyAvailability;
  if (!wa || typeof wa !== "object") return [];
  const KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const out = [];
  KEYS.forEach((key, dow) => {
    const raw = wa[key] ?? wa[key.charAt(0).toUpperCase() + key.slice(1)];
    if (!raw) return;
    const rows = Array.isArray(raw) ? raw : [raw];
    for (const item of rows) {
      if (item && typeof item === "object") {
        const st = item.start_time ?? item.startTime;
        const et = item.end_time ?? item.endTime;
        if (st && et)
          out.push({ day_of_week: dow, start_time: st, end_time: et });
      }
    }
  });
  return out;
}

/** Expand recurring windows (PT sessions, facilities) into past event occurrences. */
function expandWindowOccurrences(windows, scanStart, scanEnd) {
  if (!windows.length) return [];
  const out = [];
  for (let t = scanStart.getTime(); t < scanEnd.getTime(); t += 86400000) {
    const day = new Date(t);
    const dow = mondayIndexFromDate(day);
    for (const w of windows) {
      const wd = w.day_of_week ?? w.dayOfWeek;
      if (wd == null || wd !== dow) continue;
      const st = parseHhMm(w.start_time ?? w.startTime ?? "");
      const et = parseHhMm(w.end_time ?? w.endTime ?? "");
      const startDt = new Date(day);
      startDt.setHours(st.h, st.min, 0, 0);
      const endDt = new Date(day);
      endDt.setHours(et.h, et.min, 0, 0);
      if (endDt <= startDt || startDt.getTime() >= scanEnd.getTime()) continue;
      out.push({ start: startDt, end: endDt });
    }
  }
  return out;
}

// ─── Coach / avatar helpers ───────────────────────────────────────────────────

function extractCoachList(rawCoaches, theme) {
  if (!Array.isArray(rawCoaches)) return [];
  const seen = new Set();
  const out = [];
  for (const entry of rawCoaches) {
    const c = entry?.coachId ?? entry?.coach ?? entry;
    if (!c || typeof c !== "object") continue;
    const id = String(c._id ?? c.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const url = getAvatarUrl(c, false, theme, theme !== "dark");
    out.push({ id, name: String(c.name ?? "").trim(), avatarUrl: url });
  }
  return out;
}

function resolveBookingAvatarUrl(memberAvatar, theme) {
  if (!memberAvatar) return null;
  if (typeof memberAvatar === "string") return memberAvatar || null;
  const url = getAvatarUrl(
    { avatar: memberAvatar },
    false,
    theme,
    theme !== "dark",
  );
  return url && !url.includes("user-circle.svg") ? url : null;
}

// ─── Group events by calendar day (descending) ────────────────────────────────

function groupByDate(events) {
  const map = new Map();
  for (const ev of events) {
    const d = ev.start;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (!map.has(key)) {
      map.set(key, {
        date: new Date(d.getFullYear(), d.getMonth(), d.getDate()),
        events: [],
      });
    }
    map.get(key).events.push(ev);
  }
  const groups = [...map.values()];
  groups.sort((a, b) => b.date.getTime() - a.date.getTime());
  for (const g of groups)
    g.events.sort((a, b) => b.start.getTime() - a.start.getTime());
  return groups;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AvatarBubble({ name, avatarUrl, size = 32 }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="scheduling-history-avatar scheduling-history-avatar-img"
        style={{ width: size, height: size }}
        title={name}
      />
    );
  }
  return (
    <span
      className="scheduling-history-avatar"
      style={{ width: size, height: size, fontSize: Math.floor(size * 0.38) }}
      title={name}
    >
      {name ? name.charAt(0).toUpperCase() : "?"}
    </span>
  );
}

function ClassCard({ event, onMarkAttendance, canManage = true }) {
  const shownCoaches = (event.coaches || []).slice(0, 4);
  const overflow =
    event.extraCoachCount ?? Math.max(0, (event.coaches || []).length - 4);

  return (
    <article className="scheduling-history-event-card">
      <div className="scheduling-history-card-top">
        <div className="scheduling-history-thumbnail">
          {event.thumbnailUrl ? (
            <img
              src={event.thumbnailUrl}
              alt={event.title}
              className="scheduling-history-thumbnail-img"
            />
          ) : (
            <div className="scheduling-history-thumbnail-placeholder" />
          )}
        </div>
        <div className="scheduling-history-event-body">
          <h3 className="scheduling-history-event-title">{event.title}</h3>
          {event.description && (
            <p className="scheduling-history-event-desc">{event.description}</p>
          )}
          <div className="scheduling-history-meta">
            {/* Col 1: date / time */}
            <div className="scheduling-history-meta-stack">
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-calendar.svg" alt="" />
                {event.dateLabel}
              </span>
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-clock.svg" alt="" />
                {event.timeRange}
              </span>
            </div>
            {/* Col 2: duration / frequency */}
            <div className="scheduling-history-meta-stack">
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-clock.svg" alt="" />
                {event.duration}
              </span>
              {event.frequency && (
                <span className="scheduling-history-meta-item">
                  <img
                    src="/img/new-icons/vuesax-linear-refresh-2.svg"
                    alt=""
                  />
                  {event.frequency}
                </span>
              )}
            </div>
            {/* Col 3: location / booked */}
            <div className="scheduling-history-meta-stack">
              {event.location && (
                <span className="scheduling-history-meta-item">
                  <img src="/img/new-icons/map-pin.svg" alt="" />
                  {event.location}
                </span>
              )}
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-user.svg" alt="" />
                {event.type === "class"
                  ? `${event.booked ?? 0}${event.totalSpots != null ? `/${event.totalSpots}` : ""} Booked`
                  : (() => {
                      const n = event.booked ?? 0;
                      return `${n} ${n === 1 ? "Booking" : "Bookings"}`;
                    })()}
              </span>
            </div>
            {/* Col 4: attended / no shows */}
            <div className="scheduling-history-meta-stack">
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-user.svg" alt="" />
                {event.attended ?? 0} Attended
              </span>
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-user.svg" alt="" />
                {event.noShows ?? 0} No shows
              </span>
            </div>
            {/* Col 5: coaches */}
            {(shownCoaches.length > 0 || overflow > 0) && (
              <div className="scheduling-history-coaches">
                <span className="scheduling-history-coaches-label">
                  Coaches:
                </span>
                <div className="scheduling-history-coaches-row">
                  {shownCoaches.map((c, i) => (
                    <AvatarBubble
                      key={i}
                      name={c.name}
                      avatarUrl={c.avatarUrl}
                      size={28}
                    />
                  ))}
                  {overflow > 0 && (
                    <span className="scheduling-history-coaches-overflow">
                      +{overflow}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="scheduling-history-card-actions">
          {canManage && event.canMarkAttendance && (
            <button
              type="button"
              className="scheduling-history-mark-btn"
              onClick={() => onMarkAttendance?.(event)}
            >
              <img src="/img/new-icons/attendance-scheduling.svg" alt="" />
              Mark Attendance
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className="scheduling-history-chevron"
              aria-label="View details"
              onClick={() => {
                const kind = event.type === "facility" ? "facility" : "class";
                if (event.rawId)
                  navigateToSchedulingTabEdit(kind, String(event.rawId));
              }}
            >
              <img src="/img/new-icons/arrow-right.svg" alt="" />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function PtSessionCard({ event, onMarkAttendance, canManage = true }) {
  const shownCoaches = (event.coaches || []).slice(0, 3);
  const count = shownCoaches.length || 1;
  const thumbSize = count === 1 ? 72 : 42;

  return (
    <article className="scheduling-history-event-card">
      <div className="scheduling-history-card-top">
        <div className="scheduling-history-pt-thumb" data-count={count}>
          <div className="scheduling-history-pt-thumb-cluster">
            {shownCoaches.length > 0 ? (
              shownCoaches.map((c, i) => (
                <AvatarBubble
                  key={i}
                  name={c.name}
                  avatarUrl={c.avatarUrl}
                  size={thumbSize}
                />
              ))
            ) : (
              <AvatarBubble
                name={event.title}
                avatarUrl={null}
                size={thumbSize}
              />
            )}
          </div>
        </div>
        <div className="scheduling-history-event-body">
          <h3 className="scheduling-history-event-title">{event.title}</h3>
          <p className="scheduling-history-pt-sub">
            {event.duration}
            {event.ratio && <> &bull; {event.ratio}</>}
          </p>
          <div className="scheduling-history-meta">
            <div className="scheduling-history-meta-stack">
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-calendar.svg" alt="" />
                {event.dateLabel}
              </span>
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-clock.svg" alt="" />
                {event.timeRange}
              </span>
            </div>
            <div className="scheduling-history-meta-stack">
              <span className="scheduling-history-meta-item">
                <img src="/img/new-icons/vuesax-linear-clock.svg" alt="" />
                {event.duration}
              </span>
              {event.ratio && (
                <span className="scheduling-history-meta-item">
                  <img src="/img/new-icons/vuesax-linear-user.svg" alt="" />
                  {event.ratio}
                </span>
              )}
            </div>
            {event.location && (
              <div className="scheduling-history-meta-stack">
                <span className="scheduling-history-meta-item">
                  <img src="/img/new-icons/map-pin.svg" alt="" />
                  {event.location}
                </span>
                {event.attended != null && event.attended > 0 && (
                  <span className="scheduling-history-meta-item scheduling-history-meta-item--attended">
                    <img
                      src="/img/new-icons/vuesax-linear-tick-circle.svg"
                      alt=""
                    />
                    Attended
                  </span>
                )}
              </div>
            )}
            {(event.participants || []).length > 0 && (
              <div className="scheduling-history-participants">
                {event.participants.map((p, i) => (
                  <div key={i} className="scheduling-history-participant">
                    <AvatarBubble
                      name={p.name}
                      avatarUrl={p.avatarUrl}
                      size={32}
                    />
                    <div className="scheduling-history-participant-text">
                      <span className="scheduling-history-participant-name">
                        {p.name}
                      </span>
                      <span className="scheduling-history-participant-role">
                        {p.role ?? "Participant"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="scheduling-history-card-actions">
          {canManage && event.canMarkAttendance && (
            <button
              type="button"
              className="scheduling-history-mark-btn"
              onClick={() => onMarkAttendance?.(event)}
            >
              <img src="/img/new-icons/attendance-scheduling.svg" alt="" />
              Mark Attendance
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className="scheduling-history-chevron"
              aria-label="View details"
              onClick={() => {
                if (event.rawId)
                  navigateToSchedulingTabEdit("pt", String(event.rawId));
              }}
            >
              <img src="/img/new-icons/arrow-right.svg" alt="" />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const SchedulingHistory = ({ hasNonPullScheduling = true }) => {
  const { locals } = useRillContext();
  const activeUser = locals.activeUser;
  const theme = activeUser.webAppTheme || "dark";
  // Facilities are hidden when only PULL scheduling is enabled.
  const visibleFilterTabs = hasNonPullScheduling
    ? FILTER_TABS
    : FILTER_TABS.filter((t) => t.id !== "facilities");

  const [activeFilter, setActiveFilter] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [historyGroups, setHistoryGroups] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [attendanceEvent, setAttendanceEvent] = React.useState(null);
  const [ptAttendanceEvent, setPtAttendanceEvent] = React.useState(null);
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");
  const [selectedCoachIds, setSelectedCoachIds] = React.useState(new Set());
  const [dateDropdownOpen, setDateDropdownOpen] = React.useState(false);
  const [openDatePicker, setOpenDatePicker] = React.useState(null);
  const [coachDropdownOpen, setCoachDropdownOpen] = React.useState(false);
  const [moreFilterOpen, setMoreFilterOpen] = React.useState(false);
  const [statusFilters, setStatusFilters] = React.useState(new Set());
  const [durationMin, setDurationMin] = React.useState("");
  const [durationMax, setDurationMax] = React.useState("");
  const dateDropdownRef = React.useRef(null);
  const coachDropdownRef = React.useRef(null);
  const moreFilterRef = React.useRef(null);

  const toggleStatus = (id) =>
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const moreFilterCount =
    statusFilters.size + (durationMin ? 1 : 0) + (durationMax ? 1 : 0);

  const loadHistory = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const now = new Date();
      const scanEnd = new Date(now);
      // Show today + past 7 days only
      const scanStart = addDays(now, -7);
      scanStart.setHours(0, 0, 0, 0);

      const safeJson = async (res) => {
        try {
          return await res.json();
        } catch {
          return null;
        }
      };

      const [classesRes, ptRes, facilityRes, bookingsRes] = await Promise.all([
        fetch("/actions/scheduling/classes", { credentials: "same-origin" }),
        fetch("/actions/scheduling/pt-sessions", {
          credentials: "same-origin",
        }),
        fetch("/actions/scheduling/facilities", { credentials: "same-origin" }),
        fetch("/actions/scheduling/coach/bookings", {
          credentials: "same-origin",
        }),
      ]);

      const [classesData, ptData, facilityData, bookingsData] =
        await Promise.all([
          safeJson(classesRes),
          safeJson(ptRes),
          safeJson(facilityRes),
          safeJson(bookingsRes),
        ]);

      const classes = Array.isArray(classesData) ? classesData : [];
      const ptSessions = Array.isArray(ptData) ? ptData : [];
      const facilities = Array.isArray(facilityData) ? facilityData : [];
      const bookings = Array.isArray(bookingsData) ? bookingsData : [];

      // PT sessions and facilities rarely embed windows in the list response —
      // fetch per-item availability-windows (same approach as the calendar view).
      const ptAvailability = {};
      await Promise.all(
        ptSessions.map(async (s) => {
          const id = String(s._id ?? s.id ?? "");
          if (!id) return;
          if (Array.isArray(s.windows) && s.windows.length > 0) {
            ptAvailability[id] = { windows: s.windows };
            return;
          }
          try {
            const res = await fetch(
              `/actions/scheduling/pt-sessions/${encodeURIComponent(id)}/availability-windows`,
              { credentials: "same-origin" },
            );
            const data = await res.json().catch(() => ({}));
            if (res.ok) ptAvailability[id] = data;
          } catch {
            /* ignore */
          }
        }),
      );

      const facilityAvailability = {};
      await Promise.all(
        facilities.map(async (f) => {
          const id = String(f._id ?? f.id ?? "");
          if (!id) return;
          if (Array.isArray(f.windows) && f.windows.length > 0) {
            facilityAvailability[id] = { windows: f.windows };
            return;
          }
          try {
            const res = await fetch(
              `/actions/scheduling/facilities/${encodeURIComponent(id)}/availability-windows`,
              { credentials: "same-origin" },
            );
            const data = await res.json().catch(() => ({}));
            if (res.ok) facilityAvailability[id] = data;
          } catch {
            /* ignore */
          }
        }),
      );

      // Aggregate booking stats per event template + specific occurrence date
      const dateKeyOf = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const statsByEventDate = {};
      const statsByPtSlot = {};
      const ptBookingRecordsByEvent = {};
      for (const b of bookings) {
        const evId = String(b.bookableEventId ?? b.event_id ?? "");
        if (!evId) continue;
        const rawDate =
          b.start_datetime ??
          b.start_time ??
          b.booking_date ??
          b.event_date ??
          b.scheduled_date ??
          b.occurrence_date ??
          b.date ??
          b.start_date ??
          null;
        const parsedDate = rawDate ? new Date(rawDate) : null;
        const dateKey = parsedDate ? dateKeyOf(parsedDate) : "__nodate__";
        const key = `${evId}::${dateKey}`;
        if (!statsByEventDate[key]) {
          statsByEventDate[key] = {
            attended: 0,
            booked: 0,
            noShows: 0,
            members: [],
          };
        }
        const s = statsByEventDate[key];
        const status = String(b.status ?? "").toLowerCase();
        const member = b.member_name
          ? {
              name: String(b.member_name),
              avatarUrl: resolveBookingAvatarUrl(b.member_avatar, theme),
              role: "Participant",
            }
          : null;
        const recordStats = (target) => {
          if (status === "attended") target.attended++;
          if (status !== "cancelled" && status !== "canceled") target.booked++;
          if (status === "no_show" || status === "absent") target.noShows++;
          if (member) target.members.push(member);
        };
        recordStats(s);

        const bookingRange = getPtBookingRange(b);
        const isPtBooking =
          Boolean(b.ptSessionInstanceId ?? b.pt_session_instance_id) ||
          ptSessions.some(
            (session) => String(session._id ?? session.id ?? "") === evId,
          );
        if (isPtBooking && bookingRange) {
          const slotKey = ptSessionSlotKey(
            evId,
            bookingRange.start,
            bookingRange.end,
          );
          if (slotKey) {
            if (!statsByPtSlot[slotKey]) {
              statsByPtSlot[slotKey] = {
                attended: 0,
                booked: 0,
                noShows: 0,
                members: [],
              };
            }
            recordStats(statsByPtSlot[slotKey]);
          }
          if (!ptBookingRecordsByEvent[evId])
            ptBookingRecordsByEvent[evId] = [];
          ptBookingRecordsByEvent[evId].push({
            start: bookingRange.start,
            end: bookingRange.end,
            status,
            member,
          });
        }
      }
      const getStats = (eventId, occDate) =>
        statsByEventDate[`${eventId}::${dateKeyOf(occDate)}`] ??
        statsByEventDate[`${eventId}::__nodate__`] ??
        {};
      const getPtStats = (eventId, occurrence) => {
        const key = ptSessionSlotKey(eventId, occurrence.start, occurrence.end);
        if (key && statsByPtSlot[key]) return statsByPtSlot[key];
        const records = ptBookingRecordsByEvent[eventId] ?? [];
        const matching = records.filter(
          (record) =>
            record.start.getTime() >= occurrence.start.getTime() &&
            record.end.getTime() <= occurrence.end.getTime(),
        );
        if (matching.length === 0) return {};
        const stats = { attended: 0, booked: 0, noShows: 0, members: [] };
        for (const record of matching) {
          if (record.status === "attended") stats.attended++;
          if (record.status !== "cancelled" && record.status !== "canceled")
            stats.booked++;
          if (record.status === "no_show" || record.status === "absent")
            stats.noShows++;
          if (record.member) stats.members.push(record.member);
        }
        return stats;
      };

      const allEvents = [];

      // ── Classes ────────────────────────────────────────────────────────────
      for (const c of classes) {
        if (c.status === "cancelled" || c.status === "deleted") continue;
        const id = String(c._id ?? c.id ?? "");
        const coaches = extractCoachList(c.coaches ?? [], theme);
        const enrolled =
          c.enrolled_count ??
          c.enrolledCount ??
          c.bookings_count ??
          c.bookingsCount ??
          c.current_bookings ??
          c.spots_taken ??
          null;

        for (const occ of expandClassOccurrences(c, scanStart, scanEnd)) {
          const st = getStats(id, occ.start);
          allEvents.push({
            id: `class-${id}-${occ.start.toISOString()}`,
            rawId: id,
            coachId: c.coachId,
            type: "class",
            title: c.name ?? "Class",
            description: c.description ?? "",
            thumbnailUrl: c.thumbnail_url || null,
            canMarkAttendance: true,
            start: occ.start,
            end: occ.end,
            dateLabel: formatEventDateLabel(occ.start),
            timeRange: `${formatAmPm(occ.start)} - ${formatAmPm(occ.end)}`,
            duration: formatDuration(c.duration_minutes),
            durationMinutes:
              c.duration_minutes != null ? Number(c.duration_minutes) : null,
            frequency:
              RECURRENCE_LABELS[c.recurrence_type] ?? c.recurrence_type ?? null,
            location: c.location || null,
            attended: st.attended != null ? st.attended : null,
            booked:
              enrolled != null
                ? Number(enrolled)
                : st.booked != null
                  ? st.booked
                  : null,
            totalSpots: c.capacity != null ? Number(c.capacity) : null,
            noShows: st.noShows != null ? st.noShows : null,
            coaches,
            extraCoachCount: Math.max(0, coaches.length - 4),
          });
        }
      }

      // ── PT sessions ────────────────────────────────────────────────────────
      for (const s of ptSessions) {
        if (s.status === "cancelled" || s.status === "deleted") continue;
        const id = String(s._id ?? s.id ?? "");
        const availPayload = ptAvailability[id] ?? {};
        const windows =
          extractWindows(s).length > 0
            ? extractWindows(s)
            : extractWindows(availPayload);
        const coaches = extractCoachList(s.coaches ?? [], theme);
        const coachCount = Math.max(1, coaches.length);
        const capacityNum =
          s.capacity != null && Number(s.capacity) > 0 ? Number(s.capacity) : 1;
        const ratio =
          s.ratio_label ?? s.ratio ?? `${coachCount}:${capacityNum}`;

        for (const occ of expandWindowOccurrences(
          windows,
          scanStart,
          scanEnd,
        )) {
          const st = getPtStats(id, occ);
          const participants = (st.members ?? []).slice(0, 10);
          const durMin =
            s.duration_minutes != null
              ? Number(s.duration_minutes)
              : Math.round((occ.end.getTime() - occ.start.getTime()) / 60000);
          allEvents.push({
            id: `pt-${id}-${occ.start.toISOString()}`,
            rawId: id,
            coachId: s.coachId,
            type: "pt-session",
            title: s.name ?? s.title ?? "PT Session",
            description: s.description ?? "",
            thumbnailUrl: null,
            canMarkAttendance: true,
            start: occ.start,
            end: occ.end,
            dateLabel: formatEventDateLabel(occ.start),
            timeRange: `${formatAmPm(occ.start)} - ${formatAmPm(occ.end)}`,
            duration: formatDuration(durMin, true),
            durationMinutes: durMin != null ? Number(durMin) : null,
            frequency: null,
            location: s.location || null,
            ratio,
            attended: st.attended != null ? st.attended : null,
            coaches,
            participants,
          });
        }
      }

      // ── Facilities ─────────────────────────────────────────────────────────
      for (const f of facilities) {
        if (f.status === "cancelled" || f.status === "deleted") continue;
        const id = String(f._id ?? f.id ?? "");
        const availPayload = facilityAvailability[id] ?? {};
        const windows =
          extractWindows(f).length > 0
            ? extractWindows(f)
            : extractWindows(availPayload);
        const coaches = extractCoachList(f.coaches ?? [], theme);
        const thumbnailUrl = f.thumbnail_url ?? f.facility_image_url ?? null;
        const enrolled =
          f.enrolled_count ?? f.enrolledCount ?? f.bookings_count ?? null;

        for (const occ of expandWindowOccurrences(
          windows,
          scanStart,
          scanEnd,
        )) {
          const st = getStats(id, occ.start);
          const durMin =
            f.duration_minutes != null
              ? Number(f.duration_minutes)
              : Math.round((occ.end.getTime() - occ.start.getTime()) / 60000);
          allEvents.push({
            id: `facility-${id}-${occ.start.toISOString()}`,
            rawId: id,
            coachId: f.coachId,
            type: "facility",
            title: f.name ?? "Facility",
            description: f.description ?? "",
            thumbnailUrl,
            canMarkAttendance: true,
            start: occ.start,
            end: occ.end,
            dateLabel: formatEventDateLabel(occ.start),
            timeRange: `${formatAmPm(occ.start)} - ${formatAmPm(occ.end)}`,
            duration: formatDuration(durMin),
            durationMinutes: durMin != null ? Number(durMin) : null,
            frequency: null,
            location: f.location || null,
            attended: st.attended != null ? st.attended : null,
            booked:
              enrolled != null
                ? Number(enrolled)
                : st.booked != null
                  ? st.booked
                  : null,
            totalSpots: f.capacity != null ? Number(f.capacity) : null,
            noShows: st.noShows != null ? st.noShows : null,
            coaches,
            extraCoachCount: Math.max(0, coaches.length - 4),
          });
        }
      }

      // Sort most-recent first, cap total to 200 entries
      allEvents.sort((a, b) => b.start.getTime() - a.start.getTime());
      const capped = allEvents.slice(0, 200);
      setHistoryGroups(groupByDate(capped));
    } catch (e) {
      setLoadError(e?.message ?? "Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [theme]);

  React.useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (
        dateDropdownRef.current &&
        !dateDropdownRef.current.contains(e.target)
      ) {
        setDateDropdownOpen(false);
        setOpenDatePicker(null);
      }
      if (
        coachDropdownRef.current &&
        !coachDropdownRef.current.contains(e.target)
      ) {
        setCoachDropdownOpen(false);
      }
      if (moreFilterRef.current && !moreFilterRef.current.contains(e.target)) {
        setMoreFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allCoaches = React.useMemo(() => {
    const seen = new Set();
    const coaches = [];
    for (const g of historyGroups) {
      for (const e of g.events) {
        for (const c of e.coaches || []) {
          if (!seen.has(c.id)) {
            seen.add(c.id);
            coaches.push(c);
          }
        }
      }
    }
    coaches.sort((a, b) => a.name.localeCompare(b.name));
    return coaches;
  }, [historyGroups]);

  function toggleCoach(id) {
    setSelectedCoachIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const filteredGroups = React.useMemo(() => {
    const fromDate = dateFrom ? parseYyyyMmDd(dateFrom) : null;
    const toDate = dateTo ? parseYyyyMmDd(dateTo) : null;
    if (toDate) toDate.setHours(23, 59, 59, 999);
    const minDur = durationMin ? Number(durationMin) : null;
    const maxDur = durationMax ? Number(durationMax) : null;
    const now = Date.now();

    return historyGroups
      .map((g) => ({
        ...g,
        events: g.events.filter((e) => {
          const typeMatch =
            activeFilter === "all" ||
            (activeFilter === "classes" && e.type === "class") ||
            (activeFilter === "pt-sessions" && e.type === "pt-session") ||
            (activeFilter === "facilities" && e.type === "facility");
          const searchMatch =
            !searchQuery ||
            e.title.toLowerCase().includes(searchQuery.toLowerCase());
          const dateMatch =
            (!fromDate || e.start >= fromDate) &&
            (!toDate || e.start <= toDate);
          const coachMatch =
            selectedCoachIds.size === 0 ||
            (e.coaches || []).some((c) => selectedCoachIds.has(c.id));
          const statusMatch =
            statusFilters.size === 0 ||
            (statusFilters.has("attended") && (e.attended ?? 0) > 0) ||
            (statusFilters.has("no-shows") && (e.noShows ?? 0) > 0) ||
            (statusFilters.has("upcoming") && e.start.getTime() > now);
          const durationMatch =
            (minDur == null ||
              (e.durationMinutes != null && e.durationMinutes >= minDur)) &&
            (maxDur == null ||
              (e.durationMinutes != null && e.durationMinutes <= maxDur));
          return (
            typeMatch &&
            searchMatch &&
            dateMatch &&
            coachMatch &&
            statusMatch &&
            durationMatch
          );
        }),
      }))
      .filter((g) => g.events.length > 0);
  }, [
    historyGroups,
    activeFilter,
    searchQuery,
    dateFrom,
    dateTo,
    selectedCoachIds,
    statusFilters,
    durationMin,
    durationMax,
  ]);

  if (ptAttendanceEvent) {
    return (
      <MarkAttendancePtView
        event={ptAttendanceEvent}
        onBack={() => setPtAttendanceEvent(null)}
        onEdit={(ev) => {
          const id = ev?.rawId ?? ev?.raw?._id ?? ev?.raw?.id;
          if (id) navigateToSchedulingTabEdit("pt", String(id));
        }}
        theme={theme}
      />
    );
  }

  if (attendanceEvent) {
    return (
      <MarkAttendanceView
        event={attendanceEvent}
        onBack={() => setAttendanceEvent(null)}
        onEdit={(ev) => {
          const id = ev?.raw?._id ?? ev?.raw?.id;
          if (id) navigateToSchedulingTabEdit(ev.kind, String(id));
        }}
        theme={theme}
      />
    );
  }

  return (
    <div className="scheduling-history">
      <header className="scheduling-classes-header">
        <hgroup className="scheduling-classes-title-group">
          <h1 className="scheduling-classes-title">History</h1>
          <p className="scheduling-classes-subtitle">
            Review past sessions, classes, and activities to track attendance
            and performance.
          </p>
        </hgroup>
      </header>

      <div className="scheduling-bookings-filter-tabs scheduling-history-tabs">
        {visibleFilterTabs.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className="scheduling-bookings-filter-tab scheduling-history-tab"
            aria-current={activeFilter === id ? "page" : undefined}
            onClick={() => setActiveFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="scheduling-history-content-box">
        <div className="scheduling-history-search-row">
          <div className="scheduling-history-search-wrap">
            <img
              src="/img/new-icons/search.svg"
              alt=""
              className="scheduling-history-search-icon"
            />
            <input
              type="text"
              className="scheduling-history-search-input"
              placeholder="Search by Class, PT Sessions, Facilities.."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div ref={dateDropdownRef} className="scheduling-history-filter-wrap">
            <button
              type="button"
              className={`scheduling-history-filter-dropdown${dateFrom || dateTo ? " scheduling-history-filter-dropdown--active" : ""}`}
              onClick={() => {
                setDateDropdownOpen((o) => !o);
                setCoachDropdownOpen(false);
              }}
            >
              <img src="/img/new-icons/vuesax-linear-calendar.svg" alt="" />
              {dateFrom || dateTo
                ? `${formatDateDisplay(dateFrom) || "…"} → ${formatDateDisplay(dateTo) || "…"}`
                : "Filter by date"}
              <img src="/img/new-icons/chevron-down.svg" alt="" />
            </button>
            {dateDropdownOpen && (
              <div className="scheduling-history-filter-panel">
                <div className="scheduling-history-filter-panel-date">
                  <div className="scheduling-history-filter-label">
                    From
                    <button
                      type="button"
                      className="scheduling-history-filter-date-input scheduling-history-filter-date-trigger"
                      onClick={() =>
                        setOpenDatePicker((p) => (p === "from" ? null : "from"))
                      }
                    >
                      {formatDateDisplay(dateFrom) || (
                        <span className="scheduling-history-filter-date-placeholder">
                          MMM d, yyyy
                        </span>
                      )}
                    </button>
                    {openDatePicker === "from" && (
                      <DayPicker
                        mode="single"
                        selected={parseYyyyMmDd(dateFrom) ?? undefined}
                        toDate={parseYyyyMmDd(dateTo) ?? undefined}
                        onSelect={(d) => {
                          setDateFrom(dateToYyyyMmDd(d));
                          setOpenDatePicker(null);
                        }}
                        defaultMonth={parseYyyyMmDd(dateFrom) ?? new Date()}
                        showOutsideDays
                        components={{ Caption: HistoryCalendarCaption }}
                        modifiersClassNames={{ selected: "my-selected" }}
                      />
                    )}
                  </div>
                  <div className="scheduling-history-filter-label">
                    To
                    <button
                      type="button"
                      className="scheduling-history-filter-date-input scheduling-history-filter-date-trigger"
                      onClick={() =>
                        setOpenDatePicker((p) => (p === "to" ? null : "to"))
                      }
                    >
                      {formatDateDisplay(dateTo) || (
                        <span className="scheduling-history-filter-date-placeholder">
                          MMM d, yyyy
                        </span>
                      )}
                    </button>
                    {openDatePicker === "to" && (
                      <DayPicker
                        mode="single"
                        selected={parseYyyyMmDd(dateTo) ?? undefined}
                        fromDate={parseYyyyMmDd(dateFrom) ?? undefined}
                        onSelect={(d) => {
                          setDateTo(dateToYyyyMmDd(d));
                          setOpenDatePicker(null);
                        }}
                        defaultMonth={parseYyyyMmDd(dateTo) ?? new Date()}
                        showOutsideDays
                        components={{ Caption: HistoryCalendarCaption }}
                        modifiersClassNames={{ selected: "my-selected" }}
                      />
                    )}
                  </div>
                </div>
                {(dateFrom || dateTo) && (
                  <button
                    type="button"
                    className="scheduling-history-filter-clear"
                    onClick={() => {
                      setDateFrom("");
                      setDateTo("");
                      setOpenDatePicker(null);
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>

          <div
            ref={coachDropdownRef}
            className="scheduling-history-filter-wrap"
          >
            <button
              type="button"
              className={`scheduling-history-filter-dropdown${selectedCoachIds.size > 0 ? " scheduling-history-filter-dropdown--active" : ""}`}
              onClick={() => {
                setCoachDropdownOpen((o) => !o);
                setDateDropdownOpen(false);
              }}
            >
              <img src="/img/new-icons/vuesax-linear-user.svg" alt="" />
              {selectedCoachIds.size > 0
                ? `${selectedCoachIds.size} Coach${selectedCoachIds.size !== 1 ? "es" : ""}`
                : "Filter by Coach"}
              <img src="/img/new-icons/chevron-down.svg" alt="" />
            </button>
            {coachDropdownOpen && (
              <div className="scheduling-history-filter-panel">
                {allCoaches.length === 0 ? (
                  <p className="scheduling-history-filter-empty">
                    No coaches found
                  </p>
                ) : (
                  <ul className="scheduling-history-filter-coaches-list">
                    {allCoaches.map((c) => (
                      <li key={c.id}>
                        <label className="scheduling-history-filter-coach-row">
                          <input
                            type="checkbox"
                            checked={selectedCoachIds.has(c.id)}
                            onChange={() => toggleCoach(c.id)}
                          />
                          <AvatarBubble
                            name={c.name}
                            avatarUrl={c.avatarUrl}
                            size={24}
                          />
                          <span>{c.name || "Unknown"}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                {selectedCoachIds.size > 0 && (
                  <button
                    type="button"
                    className="scheduling-history-filter-clear"
                    onClick={() => setSelectedCoachIds(new Set())}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
          <div ref={moreFilterRef} className="scheduling-classes-filter-wrap">
            <button
              type="button"
              className="scheduling-classes-filter-btn"
              aria-expanded={moreFilterOpen}
              onClick={() => {
                setMoreFilterOpen((o) => !o);
                setDateDropdownOpen(false);
                setCoachDropdownOpen(false);
              }}
            >
              <img src="/img/new-icons/filter-white.svg" alt="" />
              Filter
              {moreFilterCount > 0 && (
                <span className="scheduling-classes-filter-badge">
                  {moreFilterCount}
                </span>
              )}
            </button>
            {moreFilterOpen && (
              <div className="scheduling-classes-filter-panel" role="dialog">
                <div className="scheduling-classes-filter-label">
                  Status
                  <div className="scheduling-classes-filter-checks">
                    {[
                      { id: "attended", label: "Attended" },
                      { id: "no-shows", label: "No shows" },
                      { id: "upcoming", label: "Upcoming" },
                    ].map((s) => (
                      <label
                        key={s.id}
                        className="scheduling-classes-filter-check"
                      >
                        <input
                          type="checkbox"
                          checked={statusFilters.has(s.id)}
                          onChange={() => toggleStatus(s.id)}
                        />
                        <span>{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="scheduling-classes-filter-row">
                  <label className="scheduling-classes-filter-label">
                    Min duration (min)
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 30"
                      value={durationMin}
                      onChange={(e) => setDurationMin(e.target.value)}
                    />
                  </label>
                  <label className="scheduling-classes-filter-label">
                    Max duration (min)
                    <input
                      type="number"
                      min="0"
                      className="scheduling-classes-filter-input"
                      placeholder="e.g. 90"
                      value={durationMax}
                      onChange={(e) => setDurationMax(e.target.value)}
                    />
                  </label>
                </div>
                <footer className="scheduling-classes-filter-footer">
                  <button
                    type="button"
                    data-button-type="tertiary"
                    onClick={() => {
                      setStatusFilters(new Set());
                      setDurationMin("");
                      setDurationMax("");
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    data-button-type="primary"
                    onClick={() => setMoreFilterOpen(false)}
                  >
                    Done
                  </button>
                </footer>
              </div>
            )}
          </div>
        </div>

        <div className="scheduling-history-events">
          {loading ? (
            <HistoryCardSkeleton theme={theme} />
          ) : loadError ? (
            <p className="scheduling-history-empty" role="alert">
              {loadError}
            </p>
          ) : filteredGroups.length === 0 ? (
            <SchedulingEmptyState
              iconSrc="/img/new-icons/history-icon.svg"
              title="No history yet"
              description="Past events and bookings will appear here once available."
            />
          ) : (
            filteredGroups.map((group) => (
              <div
                key={group.date.toISOString()}
                className="scheduling-history-group"
              >
                <div className="scheduling-history-group-label">
                  {formatGroupDateLabel(group.date)}
                </div>
                <div className="scheduling-history-group-cards">
                  {group.events.map((event) =>
                    event.type === "pt-session" ? (
                      <PtSessionCard
                        key={event.id}
                        event={event}
                        canManage={canManageSchedulingRecord(event, activeUser)}
                        onMarkAttendance={setPtAttendanceEvent}
                      />
                    ) : (
                      <ClassCard
                        key={event.id}
                        event={event}
                        canManage={canManageSchedulingRecord(event, activeUser)}
                        onMarkAttendance={setAttendanceEvent}
                      />
                    ),
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulingHistory;
